import { ORDER_SORTS, ORDER_STATUSES, RETAILERS, STATUS_LABELS, type Order, type OrderSort } from '@nebueye/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { z } from 'zod'
import { ProductThumb, RetailerMark } from '@/components/app/retailer'
import { SearchBox } from '@/components/app/search-box'
import { EmptyState, ErrorState, OrbitArt } from '@/components/app/states'
import { StatusBadge } from '@/components/app/status-badge'
import { ColumnHeader, PageTitle, Pager, RetailerTabs, RowChevron, Surface, rowClass, rowEnter } from '@/components/app/surface'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { ago, count, money, plural } from '@/lib/format'
import { ordersQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'

const PAGE = 50

const search = z.object({
  search: z.string().optional(),
  retailer: z.enum(RETAILERS).optional(),
  status: z.enum(ORDER_STATUSES).optional(),
  sort: z.enum(ORDER_SORTS).optional(),
  asc: z.boolean().optional(),
  page: z.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_app/orders/')({
  validateSearch: search,
  head: () => ({ meta: [{ title: 'Orders · Nebueye' }] }),
  component: Orders,
})

/** item · order number · status · total · placed · chevron */
const COLS = 'grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_13rem_11rem_6.5rem_7.5rem_1.25rem]'

function Orders() {
  const s = Route.useSearch()
  const navigate = Route.useNavigate()
  const page = s.page ?? 1
  const sort = s.sort ?? 'placed'
  const set = (patch: Partial<z.infer<typeof search>>, resetPage = true) =>
    navigate({ search: (prev) => ({ ...prev, ...(resetPage ? { page: undefined } : {}), ...patch }), replace: true })

  const q = useQuery(
    ordersQuery({
      search: s.search,
      retailer: s.retailer,
      status: s.status,
      sort,
      desc: s.asc ? 'false' : 'true',
      offset: (page - 1) * PAGE,
      limit: PAGE,
    }),
  )
  const data = q.data
  const filtered = !!(s.search || s.status || s.retailer)
  const allCount = data ? Object.values(data.retailerCounts).reduce((a, b) => a + (b ?? 0), 0) : 0
  const onSort = (f: OrderSort) =>
    f === sort ? set({ asc: s.asc ? undefined : true }) : set({ sort: f === 'placed' ? undefined : f, asc: undefined })
  const clear = () => navigate({ search: {}, replace: true })

  return (
    <div className="space-y-6">
      <PageTitle
        title="Orders"
        description={data ? (filtered ? `${plural(data.total, 'order')} match your filters` : `${count(allCount)} orders across your inboxes`) : 'Every order from your inboxes'}
        action={
          <SearchBox
            value={s.search ?? ''}
            onChange={(v) => set({ search: v || undefined })}
            placeholder="Order #, email, tracking or item"
            label="Search orders"
            shortcut
            className="w-full sm:w-80"
          />
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <RetailerTabs value={s.retailer} counts={data?.retailerCounts} onChange={(r) => set({ retailer: r })} />
        <div className="flex items-center gap-2">
          {filtered && (
            <Button variant="ghost" size="sm" onClick={clear}>
              Clear filters
            </Button>
          )}
          <Select value={s.status ?? 'all'} onValueChange={(v) => set({ status: v === 'all' ? undefined : (v as typeof s.status) })}>
            <SelectTrigger size="sm" className="bg-card w-44" aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="all">All statuses</SelectItem>
              {ORDER_STATUSES.map((st) => (
                <SelectItem key={st} value={st}>
                  {STATUS_LABELS[st]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Surface aria-label="Orders">
        <ColumnHeader cols={COLS}>
          <span>Item</span>
          <span>Order</span>
          <SortButton label="Status" field="status" sort={sort} asc={!!s.asc} onSort={onSort} />
          <SortButton label="Total" field="total" sort={sort} asc={!!s.asc} onSort={onSort} className="justify-self-end" />
          <SortButton label="Placed" field="placed" sort={sort} asc={!!s.asc} onSort={onSort} className="justify-self-end" />
          <span />
        </ColumnHeader>

        {q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} className="px-6" />
        ) : !data ? (
          <RowsSkeleton />
        ) : data.orders.length === 0 ? (
          <EmptyState
            className="px-6"
            art={filtered ? undefined : <OrbitArt />}
            title={filtered ? 'No orders match' : 'No orders yet'}
            action={
              filtered && (
                <Button variant="outline" onClick={clear}>
                  Clear filters
                </Button>
              )
            }
          >
            {filtered
              ? 'Try a different search, or widen the status and retailer filters.'
              : 'Every confirmation, shipment and cancellation from your inboxes will be listed here, newest first.'}
          </EmptyState>
        ) : (
          <ul className={cn('transition-opacity duration-150', q.isPlaceholderData && 'opacity-50')}>
            {data.orders.map((o, i) => (
              <li key={o.id} className="border-b last:border-b-0">
                <OrderRow o={o} index={i} />
              </li>
            ))}
          </ul>
        )}

        {data && <Pager page={page} pageSize={PAGE} total={data.total} noun="order" onPage={(p) => set({ page: p === 1 ? undefined : p }, false)} />}
      </Surface>
    </div>
  )
}

function SortButton({
  label,
  field,
  sort,
  asc,
  onSort,
  className,
}: {
  label: string
  field: OrderSort
  sort: OrderSort
  asc: boolean
  onSort: (f: OrderSort) => void
  className?: string
}) {
  const active = sort === field
  const Icon = asc ? ArrowUp : ArrowDown
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      aria-label={`Sort by ${label.toLowerCase()}${active ? (asc ? ', ascending' : ', descending') : ''}`}
      className={cn('hover:text-foreground inline-flex items-center gap-1 transition-colors', active && 'text-foreground', className)}
    >
      {label}
      {active && <Icon className="size-3.5" />}
    </button>
  )
}

function OrderRow({ o, index }: { o: Order; index: number }) {
  const item = o.items[0]
  const units = o.items.reduce((s, i) => s + i.quantity, 0)
  const enter = rowEnter(index)
  return (
    <Link
      to="/orders/$orderNumber"
      params={{ orderNumber: o.orderNumber }}
      search={{ retailer: o.retailer }}
      style={enter.style}
      className={cn(rowClass, enter.className, COLS, 'py-3')}
    >
      <div className="flex min-w-0 items-center gap-3.5">
        <ProductThumb src={item?.imageUrl} retailer={o.retailer} className="size-10 rounded-lg text-xs" />
        <div className="min-w-0 space-y-0.5">
          <p className="truncate text-sm font-medium">{item?.name ?? `Order ${o.orderNumber}`}</p>
          <p className="text-muted-foreground flex items-center gap-1.5 text-[13px]">
            <RetailerMark retailer={o.retailer} className="size-4 rounded text-[7px]" />
            <span className="truncate">
              {units > 1 && <span className="tabular">{units} units · </span>}
              {o.email}
            </span>
          </p>
        </div>
      </div>

      <p className="id-mono text-muted-foreground hidden truncate lg:block">{o.orderNumber}</p>

      <div className="order-3 col-span-2 flex items-center gap-2 lg:order-none lg:col-span-1 lg:block">
        <StatusBadge status={o.status} />
        {o.cancelReason && <p className="text-subtle text-xs lg:mt-1">{o.cancelReason}</p>}
        <span className="text-subtle ml-auto text-xs lg:hidden">{ago(o.orderDate)}</span>
      </div>

      <p className="tabular text-right text-sm font-medium">{money(o.total)}</p>
      <p className="text-muted-foreground hidden text-right text-[13px] whitespace-nowrap lg:block">{ago(o.orderDate)}</p>
      <RowChevron />
    </Link>
  )
}

function RowsSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading orders">
      {Array.from({ length: 10 }, (_, i) => (
        <div key={i} className={cn('grid items-center gap-x-6 gap-y-3 border-b px-5 py-3 last:border-b-0', COLS)}>
          <div className="flex items-center gap-3.5">
            <Skeleton className="size-10 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-3/5 max-w-72" />
              <Skeleton className="h-3 w-40" />
            </div>
          </div>
          <Skeleton className="hidden h-3 w-32 lg:block" />
          <Skeleton className="hidden h-6 w-24 rounded-full lg:block" />
          <Skeleton className="h-3.5 w-14 justify-self-end" />
          <Skeleton className="hidden h-3 w-16 justify-self-end lg:block" />
          <span />
        </div>
      ))}
    </div>
  )
}
